import React, { useMemo, useState } from 'react';
import { MapPin, Search as SearchIcon, Home, Link as LinkIcon } from 'lucide-react';
import AppHeader from '../components/layout/AppHeader';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import { useImoveis } from '../hooks/useImoveis';
import ImovelDetailModal from '../components/imoveis/ImovelDetailModal';
import BulkAssignLoteModal from '../components/imoveis/BulkAssignLoteModal';

function fmtArea(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) + ' m²';
}

function StatusJuridicoBadge({ status }) {
  const map = {
    livre: 'bg-emerald-100 text-emerald-800',
    onerado: 'bg-amber-100 text-amber-800',
    inventario: 'bg-violet-100 text-violet-800',
    litigio: 'bg-red-100 text-red-800',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700';
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{status || '—'}</span>;
}

function proprietariosToText(list) {
  if (!list || !list.length) return '—';
  const nomes = list
    .map((p) => {
      if (p.empresa) return p.empresa.empresa || p.empresa.company_name;
      if (p.contato) return [p.contato.first_name, p.contato.last_name].filter(Boolean).join(' ');
      return null;
    })
    .filter(Boolean);
  if (nomes.length === 0) return '—';
  if (nomes.length === 1) return nomes[0];
  return `${nomes[0]} +${nomes.length - 1}`;
}

export default function ImoveisPage() {
  const { data, loading, error, refetch } = useImoveis();
  const [search, setSearch] = useState('');
  const [selectedImovelId, setSelectedImovelId] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter((i) => {
      const bag = [
        i.nome,
        i.matricula,
        i.inscricao_municipal,
        i.endereco,
        i.bairro,
        i.cidade,
        i.uf,
        i.lotes?.nome,
        i.lotes?.areas?.nome,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return bag.includes(needle);
    });
  }, [data, search]);

  const sorted = useMemo(() => {
    if (!sortField) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let va; let vb;
      switch (sortField) {
        case 'inscricao_municipal': va = a.inscricao_municipal; vb = b.inscricao_municipal; break;
        case 'matricula':    va = a.matricula; vb = b.matricula; break;
        case 'endereco':     va = a.endereco; vb = b.endereco; break;
        case 'bairro':       va = a.bairro; vb = b.bairro; break;
        case 'cidade':       va = a.cidade; vb = b.cidade; break;
        case 'uf':           va = a.uf; vb = b.uf; break;
        case 'area':         va = a.area_terreno_m2 == null ? null : Number(a.area_terreno_m2); vb = b.area_terreno_m2 == null ? null : Number(b.area_terreno_m2); break;
        case 'proprietarios': va = proprietariosToText(a.imovel_proprietarios); vb = proprietariosToText(b.imovel_proprietarios); break;
        case 'area_negocio': va = a.lotes?.areas?.nome; vb = b.lotes?.areas?.nome; break;
        case 'juridico':     va = a.status_juridico; vb = b.status_juridico; break;
        default: return 0;
      }
      if (va == null || va === '' || va === '—') va = null;
      if (vb == null || vb === '' || vb === '—') vb = null;
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc'
        ? String(va).localeCompare(String(vb), 'pt-BR', { sensitivity: 'base' })
        : String(vb).localeCompare(String(va), 'pt-BR', { sensitivity: 'base' });
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  const SortHeader = ({ field, label, align = 'text-left', width }) => (
    <th
      className={`px-3 py-2 ${align} cursor-pointer hover:text-blue-700 select-none whitespace-nowrap`}
      style={width ? { width: `${width}px` } : undefined}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === 'asc' ? '▲' : '▼')}
    </th>
  );

  const allVisibleSelected = sorted.length > 0 && sorted.every((r) => selectedIds.has(r.id));
  const handleToggleAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) sorted.forEach((r) => next.add(r.id));
      else sorted.forEach((r) => next.delete(r.id));
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader title="Imóveis" subtitle="Listagem global" icon={MapPin} />
      <main className="w-full px-4 lg:px-6 2xl:px-8 py-4 lg:py-6 space-y-3">
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <SearchIcon size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar matrícula, contribuinte, endereço, bairro, cidade, área..."
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-400"
            />
          </div>
          <div className="text-[11px] text-slate-500 ml-auto">
            {loading ? '...' : `${filtered.length}${data ? ` / ${data.length}` : ''}`}
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 flex items-center justify-between">
            <span className="text-xs text-blue-800 font-medium">
              {selectedIds.size} imóve{selectedIds.size > 1 ? 'is' : 'l'} selecionado{selectedIds.size > 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkModal(true)}
                className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
              >
                <LinkIcon size={12} />
                Vincular à Área de Negócio
              </button>
              <button
                onClick={() => setSelectedIds(new Set())}
                className="px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded"
              >
                Limpar seleção
              </button>
            </div>
          </div>
        )}

        {loading && <LoadingSpinner label="Carregando imóveis..." />}
        {!loading && error && <ErrorState message={error} onRetry={refetch} />}
        {!loading && !error && filtered.length === 0 && (
          <EmptyState
            message={search ? 'Nenhum imóvel bate com a busca' : 'Nenhum imóvel cadastrado'}
            hint="Imóveis vivem dentro de áreas → lotes. Cadastre uma área e lotes primeiro."
            icon={Home}
          />
        )}

        {!loading && !error && filtered.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[1320px]">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-center" style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300"
                        checked={allVisibleSelected}
                        onChange={(e) => handleToggleAllVisible(e.target.checked)}
                        title="Selecionar todos os imóveis visíveis"
                      />
                    </th>
                    <SortHeader field="inscricao_municipal" label="Nº Contribuinte" width={140} />
                    <SortHeader field="matricula" label="Matrícula" width={90} />
                    <SortHeader field="endereco" label="Endereço" />
                    <SortHeader field="bairro" label="Bairro" width={120} />
                    <SortHeader field="cidade" label="Cidade" width={100} />
                    <SortHeader field="uf" label="UF" width={50} align="text-center" />
                    <SortHeader field="area" label="Área Terreno" align="text-right" width={100} />
                    <SortHeader field="proprietarios" label="Proprietários" width={180} />
                    <SortHeader field="area_negocio" label="Área de Negócio" width={140} />
                    <SortHeader field="juridico" label="Jurídico" width={120} />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((i) => {
                    const isSelected = selectedIds.has(i.id);
                    return (
                      <tr
                        key={i.id}
                        onClick={() => setSelectedImovelId(i.id)}
                        className={`border-t border-slate-100 cursor-pointer ${
                          isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-blue-50'
                        }`}
                      >
                        <td
                          className="px-3 py-1.5 text-center"
                          style={{ width: '40px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="rounded border-slate-300"
                            checked={isSelected}
                            onChange={() => toggleSelectOne(i.id)}
                            aria-label={`Selecionar imóvel ${i.matricula || i.id}`}
                          />
                        </td>
                        <td className="px-3 py-1.5 font-mono text-slate-700 whitespace-nowrap">
                          {i.inscricao_municipal || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-slate-700 whitespace-nowrap">{i.matricula || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-800">
                          <div className="truncate" title={`${i.endereco || ''} ${i.numero || ''} ${i.complemento || ''}`.trim()}>
                            {[i.endereco, i.numero].filter(Boolean).join(', ') || <span className="text-slate-400">—</span>}
                          </div>
                          {i.nome && <div className="text-[10px] text-slate-400 truncate" title={i.nome}>{i.nome}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap truncate" title={i.bairro || ''}>
                          {i.bairro || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap truncate" title={i.cidade || ''}>
                          {i.cidade || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 text-center whitespace-nowrap">
                          {i.uf ? String(i.uf).toUpperCase() : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtArea(i.area_terreno_m2)}</td>
                        <td className="px-3 py-1.5 text-slate-700 truncate" title={proprietariosToText(i.imovel_proprietarios)}>
                          {proprietariosToText(i.imovel_proprietarios)}
                        </td>
                        <td className="px-3 py-1.5 text-slate-600 truncate" title={i.lotes?.areas?.nome || ''}>
                          {i.lotes?.areas?.nome || <span className="text-slate-400">— sem lote —</span>}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="truncate" title={i.onus_descricao || i.status_juridico || ''}>
                            <StatusJuridicoBadge status={i.status_juridico} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {selectedImovelId && (
        <ImovelDetailModal imovelId={selectedImovelId} onClose={() => { setSelectedImovelId(null); refetch(); }} />
      )}

      {showBulkModal && (
        <BulkAssignLoteModal
          imovelIds={Array.from(selectedIds)}
          onClose={() => setShowBulkModal(false)}
          onApplied={() => { setSelectedIds(new Set()); refetch(); }}
        />
      )}
    </div>
  );
}
